import {createPortError,safePortCode} from '../platform/port-error.js';

function required(value,label,max=120){
  const result=String(value??'').trim();
  if(!result)throw new TypeError(label+' is required');
  if(result.length>max)throw new TypeError(label+' is too long');
  return result;
}

function abortError({signal,timedOut}){
  if(signal?.aborted&&!timedOut)return createPortError('DYMEK_PROVIDER_CANCELLED',{cancelled:true});
  return createPortError('DYMEK_PROVIDER_TIMEOUT',{retryable:true,timeout:true});
}

export function createDymekServerProvider({
  client,
  functionName='dymek-assistant',
  appVersion='unknown',
  environment='dev',
  timeoutMs=20_000
}={}){
  if(typeof client?.functions?.invoke!=='function')throw new TypeError('Functions client with invoke() is required');
  const name=required(functionName,'functionName',80);
  const app=required(appVersion,'appVersion',80);
  const env=required(environment,'environment',40);
  const timeout=Math.max(10,Math.min(60_000,Number(timeoutMs)||20_000));

  async function respond(envelope,{signal}={}){
    if(!envelope||typeof envelope!=='object')throw new TypeError('Dymek request envelope is required');
    if(signal?.aborted)throw createPortError('DYMEK_PROVIDER_CANCELLED',{cancelled:true});
    const controller=new AbortController();
    let timedOut=false;
    const onAbort=()=>controller.abort();
    signal?.addEventListener?.('abort',onAbort,{once:true});
    const timer=setTimeout(()=>{
      timedOut=true;
      controller.abort();
    },timeout);

    try{
      const {data,error}=await client.functions.invoke(name,{
        body:{
          envelope,
          clientMeta:{
            appVersion:app,
            environment:env
          }
        },
        signal:controller.signal
      });
      if(error){
        if(controller.signal.aborted)throw abortError({signal,timedOut});
        throw createPortError('DYMEK_PROVIDER_UNAVAILABLE',{retryable:true});
      }
      if(!data?.ok){
        const code=safePortCode(data?.code,'DYMEK_PROVIDER_FAILED');
        throw createPortError(code,{retryable:Boolean(data?.retryable)});
      }
      if(!data.response||typeof data.response!=='object'){
        throw createPortError('DYMEK_PROVIDER_INVALID_RESPONSE');
      }
      return data.response;
    }catch(error){
      if(error?.name==='AbortError'||controller.signal.aborted){
        throw abortError({signal,timedOut});
      }
      throw error;
    }finally{
      clearTimeout(timer);
      signal?.removeEventListener?.('abort',onAbort);
    }
  }

  return Object.freeze({
    kind:'server_edge_function',
    respond
  });
}
